import React, { useState } from 'react'
import Box from '@mui/material/Box'
import { Button, FormHelperText, Step, StepLabel, Stepper } from '@mui/material'
import { AppPages, FinalSubmission, SentStatus, StepPage, AppProps } from './types'
import Quiz from './components/Quiz'
import { ContactForm } from './components/ContactForm'
import { validateInput } from './components/ContactInput'
import { SubmitPage } from './components/FinalSubmission'
import Grid from '@mui/material/Unstable_Grid2/Grid2'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

const downloadFileName = 'Cyber411 Risk Self-Assessment.pdf'
const contactStorageKey = 'cyber411-rsa-contact'
const configSteps: StepPage[] = [
  {
    page: 'contact',
    label: 'Contact Info',
  },
  {
    page: 'quiz',
    label: 'Assessment',
  },
  {
    page: 'final',
    label: 'Download Report',
  },
]

export const App = (props: AppProps) => {
  const [state, setState] = useState<AppProps>(props)
  const { executeRecaptcha } = useGoogleReCaptcha()

  // @ts-ignore
  window._app = { props, state }

  const IsMobile = () => {
    return props.windowWidth <= 768
  }

  const HandleSubmit = async () => {
    const onSend = (success: boolean) => {
      if (success) {
        setState(prevState => ({
          ...prevState,
          sentStatus: 'sent',
        }))
        ToggleStep('final', true)
      } else {
        setState(prevState => ({
          ...prevState,
          sentStatus: 'error',
        }))
      }
    }
    if (!executeRecaptcha) {
      return
    }
    const token = await executeRecaptcha('rsa_survey_submit')
    setState(prevState => ({
      ...prevState,
      sentStatus: 'sending',
    }))
    const payload: FinalSubmission = {
      token,
      contact_info: state.config.ContactFieldGroups.map(group => ({
        group_id: group.group_id,
        fields: group.fields.map(field => ({
          name: field.name,
          value: field.value,
        })),
      })),
      answers: state.config.Questions.map(question => ({
        question_id: question.question_id,
        input: question.input,
        follow_up_inputs: question.follow_up_questions ? question.follow_up_questions.map(fq => ({
          question_id: fq.question_id,
          input: fq.input,
        })) : [],
      })),
      referral: state.config.Referral,
    }
    if (process.env.NODE_ENV === 'development' && !state.api_endpoint) {
      await new Promise(r => setTimeout(r, 2000))
      console.log(payload)
      onSend(true)
    }
    if (state.config && state.api_endpoint) {
      try {
        const response = await fetch(state.api_endpoint + '/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload)
        })
        if (response.ok) {
          const blob = await response.blob()
          const url = window.URL.createObjectURL(
            new Blob([blob]),
          )
          const link = document.createElement('a')
          link.href = url
          link.setAttribute(
            'download',
            downloadFileName
          )
          document.body.appendChild(link)
          link.click()
          link.parentNode?.removeChild(link)
          onSend(true)
        } else {
          onSend(false)
        }
      } catch (err) {
        const error = err as Error
        console.error(error.message)
        onSend(false)
      }
    }
  }

  const GetSentStatus = (): SentStatus => {
    return state.sentStatus
  }

  const TogglePage = (page: AppPages) => {
    setState(prevState => ({
      ...prevState,
      currentPage: page,
    }))
  }

  const ToggleStep = (page: AppPages, completed: boolean) => {
    const currPage = state.stepPages.find(sp => sp.page === page)
    if (currPage) {
      currPage.completed = completed
      setState(prevState => ({
        ...prevState,
        stepPages: state.stepPages,
      }))
    }
  }

  const UpdateContactField = (group_id: string, name: string, value: string) => {
    found:
    for (let group of state.config.ContactFieldGroups) {
      if (group.group_id === group_id) {
        for (let field of group.fields) {
          if (field.name === name) {
            field.value = value
            break found
          }
        }
      }
    }
    setState(prevState => ({
      ...prevState,
      config: {
        ...state.config,
        ContactFieldGroups: state.config.ContactFieldGroups,
      }
    }))
    UpdateContactStore()
    if (ValidateContactFields()) {
      ShowContactValidationMessage(false)
    }
  }

  const UpdateContactStore = () => {
    const payload = state.config.ContactFieldGroups.map(group => ({
      group_id: group.group_id,
      fields: group.fields.map(field => ({
        id: field.id,
        name: field.name,
        value: field.value,
      }))
    }))
    localStorage.setItem(contactStorageKey, JSON.stringify(payload))
  }

  const ValidateContactFields = () => {
    return state.config.ContactFieldGroups.every(group => group.fields.every(field => validateInput(field) === null))
  }

  const ShowContactValidationMessage = (show: boolean) => {
    setState(prevState => ({
      ...prevState,
      showContactValidationMessage: show,
    }))
  }

  const OnCompleteQuiz = () => {
    ToggleStep('quiz', true)
    TogglePage('final')
    HandleSubmit()
  }

  const GetActiveStep = (): number => {
    if (state.stepPages.every(step => step.completed)) {
      return state.stepPages.length
    }
    return Math.max(0, state.stepPages.findIndex(step => step.page === state.currentPage))
  }

  const Reset = () => {
    setState(prevState => ({
      ...prevState,
      currentPage: 'contact',
      sentStatus: 'unsent',
      stepPages: configSteps.map(step => ({ ...step, completed: false, }))
    }))
  }
  return (
    <>
      <Box sx={{ marginBottom: '1rem' }}>
        <Stepper activeStep={GetActiveStep()}>
          {state.stepPages.map(step => {
            return (
              <Step
                key={step.page}
                completed={step.completed}
              >
                <StepLabel>{IsMobile() ? null : step.label}</StepLabel>
              </Step>
            )
          })}
        </Stepper>
      </Box>
      <Box>
        {state.currentPage === 'contact' &&
          <>
            <ContactForm
              contact_info={state.config.ContactFieldGroups}
              OnUpdateContactField={UpdateContactField}
            />
            {state.showContactValidationMessage &&
              <FormHelperText error>
                Please fill out required fields and fix any invalid inputs.
              </FormHelperText>
            }
            <Grid container justifyContent={'flex-end'}>
              <Button
                variant='contained'
                onClick={() => {
                  if (ValidateContactFields()) {
                    ToggleStep('contact', true)
                    TogglePage('quiz')
                  } else {
                    ShowContactValidationMessage(true)
                  }
                }}
              >Start</Button>
            </Grid>
          </>
        }
        {state.currentPage === 'quiz' &&
          <Quiz
            {...props}
            config={state.config}
            CancelAction={state.config.DisplayAsModal ? props.onHideModal : undefined}
            OnComplete={OnCompleteQuiz}
          />
        }
        {state.currentPage === 'final' &&
          <SubmitPage
            status={GetSentStatus()}
            onSubmit={() => HandleSubmit()}
            onReset={Reset}
            onClose={state.config.DisplayAsModal ? props.onHideModal : undefined}
            inModal={state.config.DisplayAsModal}
          />
        }
      </Box>
    </>
  )
}
